import { createContext, useContext } from 'react'

export type AppManagementContextProps = {
  /** Contains any error originated from connecting to Ditto */
  error?: Error | null
  /** App ID currently in context.*/
  appId: string
  /** True if the app is a dedicated cluster. */
  isDedicatedCluster: boolean
  /** True if the domain status is ready since the first moment the status is queried. */
  isDomainInitiallyReady: boolean
  /** True if the ingress domain for the app is ready. */
  isDomainReady: boolean
  /** True if the ingress domain for the app is in error state. */
  isDomainInError: boolean
  /** Number of times the domain status has been queried. */
  queryAttempts: number
  /** True if the streams feature is available to the user */
  allowStreams: boolean
  /** True if the viewer can update the app */
  viewerCanUpdateApp: boolean
  /** True if the viewer can access metrics */
  viewerCanAccessMetrics: boolean
  /** True if the viewer can read app data */
  viewerCanReadAppData: boolean
  /** True if the viewer can read offline licenses */
  viewerCanReadLicense: boolean
  /** True if the viewer can read API keys */
  viewerCanReadApiKey: boolean
  /** True if the viewer can access admin settings */
  viewerCanAccessAdminSettings: boolean
  /** True if the viewer can access mongo connector screen(s) */
  viewerCanAccessMongo: boolean
  /**
   * The cloud domain URL for the app
   */
  cloudDomainUrl?: string
}

const AppSettingsContext = createContext<AppManagementContextProps>({
  appId: '',
  isDomainInitiallyReady: false,
  isDedicatedCluster: false,
  isDomainReady: false,
  isDomainInError: false,
  queryAttempts: 0,
  allowStreams: false,
  viewerCanUpdateApp: false,
  viewerCanAccessMetrics: false,
  viewerCanReadAppData: false,
  viewerCanReadLicense: false,
  viewerCanAccessAdminSettings: false,
  viewerCanReadApiKey: false,
  viewerCanAccessMongo: false,
})

export const useAppManagementContext = () => useContext(AppSettingsContext)

export default AppSettingsContext
